import React, { useState, useEffect } from 'react';
import StatBox from '../ui/StatBox';
import useInterval from '../../hooks/useInterval';
import { playPopSound, playWrongSound, playMissSound, playSuccessSound } from '../../utils/audio';
import { api } from '../../utils/api';
import { User } from '../../types';
import { GameIntro, GameOver } from './GameShell';

type Bubble = {
  id: number;
  letter: string;
  x: number;
  duration: number;
  colorIndex: number;
};

type Burst = { id: number; x: number };

const BUBBLE_COLORS = [
    'from-cyan-400 to-blue-500',
    'from-pink-400 to-rose-500',
    'from-violet-400 to-purple-500',
    'from-emerald-400 to-teal-500',
    'from-amber-400 to-orange-500',
];

interface BubblePopGameProps {
  user?: User;
}

const BubblePopGame: React.FC<BubblePopGameProps> = ({ user }) => {
    const [bubbles, setBubbles] = useState<Bubble[]>([]);
    const [bursts, setBursts] = useState<Burst[]>([]);
    const [score, setScore] = useState(0);
    const [missed, setMissed] = useState(0);
    const [streak, setStreak] = useState(0);
    const [bestStreak, setBestStreak] = useState(0);
    const [timeLeft, setTimeLeft] = useState(60);
    const [gameState, setGameState] = useState<'ready' | 'playing' | 'over'>('ready');
    const keys = "asdfghjkl;";

    const startGame = () => {
        setGameState('playing');
        setScore(0);
        setMissed(0);
        setStreak(0);
        setBestStreak(0);
        setTimeLeft(60);
        setBubbles([]);
        setBursts([]);
    };

    useEffect(() => {
        if (gameState === 'over' && user && user.user_id !== 'guest') {
            api.post('/api/v1/student/game-progress', { game_name: 'bubble-pop', high_score: score, increment_attempts: true }).catch(() => {});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gameState]);

    // Bubbles spawn faster the better the player is doing -- keeps the drill challenging.
    const spawnInterval = Math.max(500, 1200 - score * 10);

    useInterval(() => {
        if (gameState !== 'playing') return;
        const newBubble: Bubble = {
            id: Date.now() + Math.random(),
            letter: keys[Math.floor(Math.random() * keys.length)],
            x: Math.random() * 90,
            duration: Math.random() * 3 + 3.5,
            colorIndex: Math.floor(Math.random() * BUBBLE_COLORS.length),
        };
        setBubbles(prev => [...prev, newBubble]);
    }, spawnInterval);

    useInterval(() => {
        if (gameState !== 'playing') return;
        if (timeLeft > 0) setTimeLeft(t => t - 1);
        else setGameState('over');
    }, 1000);

    useEffect(() => {
        if(gameState !== 'playing') return;

        const handleKeyDown = (e: KeyboardEvent) => {
            if (gameState !== 'playing') return;
            const key = e.key.toLowerCase();
            setBubbles(prev => {
                const newestBubbleWithKey = prev.filter(b => b.letter === key).sort((a,b) => b.id - a.id)[0];
                if (newestBubbleWithKey) {
                    playPopSound();
                    setScore(s => s + 1);
                    setStreak(s => {
                        const next = s + 1;
                        setBestStreak(best => Math.max(best, next));
                        if (next > 0 && next % 10 === 0) playSuccessSound();
                        return next;
                    });
                    setBursts(b => [...b, { id: newestBubbleWithKey.id, x: newestBubbleWithKey.x }]);
                    return prev.filter(b => b.id !== newestBubbleWithKey.id);
                }
                playWrongSound();
                setStreak(0);
                return prev;
            });
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [gameState]);

    if (gameState === 'ready') return (
        <GameIntro icon="🫧" title="Bubble Pop" skill="key recall -- react to a letter and hit it without looking down." accent="text-cyan-600" button="bg-gradient-to-r from-cyan-500 to-blue-500" onStart={startGame}>
            Bubbles float up from the bottom. Press the letter shown on a bubble to pop it before it escapes. Bubbles arrive faster the higher your score climbs!
        </GameIntro>
    );

    if (gameState === 'over') {
        const accuracy = (score + missed) > 0 ? Math.round((score / (score + missed)) * 100) : 100;
        const message = accuracy >= 90 ? "Incredible reflexes!" : accuracy >= 70 ? "Great job, keep practicing!" : "Nice try -- go again and beat your streak!";
        return (
            <GameOver
                message={message}
                stars={score === 0 ? 0 : accuracy >= 90 ? 3 : accuracy >= 70 ? 2 : 1}
                stats={[{ label: 'Score', value: score }, { label: 'Best Streak', value: bestStreak }, { label: 'Accuracy', value: `${accuracy}%` }]}
                button="bg-gradient-to-r from-cyan-500 to-blue-500"
                onRestart={startGame}
            />
        );
    }

    return (
        <div className="w-full">
            <div className="flex justify-around mb-4 gap-3 p-3 bg-white/60 rounded-2xl shadow-inner">
                <StatBox label="Time Left" value={timeLeft} />
                <StatBox label="Score" value={score} />
                <StatBox label="Streak" value={streak} />
                <StatBox label="Missed" value={missed} />
            </div>
            <div className="relative h-96 bg-gradient-to-b from-sky-100 to-cyan-200 rounded-lg overflow-hidden border-4 border-cyan-300 shadow-inner">
                {bubbles.map(bubble => (
                    <div key={bubble.id}
                         className={`absolute w-16 h-16 bg-gradient-to-br ${BUBBLE_COLORS[bubble.colorIndex]} rounded-full flex items-center justify-center text-white font-bold text-3xl shadow-lg ring-2 ring-white ring-opacity-50 animate-float`}
                         style={{ left: `${bubble.x}%`, animationDuration: `${bubble.duration}s`}}
                         onAnimationEnd={() => {
                            setBubbles(bs => bs.filter(b => b.id !== bubble.id));
                            setMissed(m => m + 1);
                            setStreak(0);
                            playMissSound();
                         }}
                    >
                        {bubble.letter}
                    </div>
                ))}
                {bursts.map(burst => (
                    <div key={burst.id}
                         className="absolute w-16 h-16 rounded-full border-4 border-white animate-pop-burst pointer-events-none"
                         style={{ left: `${burst.x}%`, bottom: '10%' }}
                         onAnimationEnd={() => setBursts(b => b.filter(x => x.id !== burst.id))}
                    />
                ))}
                 <style>{`
                    @keyframes float {
                        from { top: 100%; }
                        to { top: -100px; }
                    }
                    .animate-float { animation-name: float; animation-timing-function: linear; }
                    @keyframes pop-burst {
                        from { transform: scale(0.6); opacity: 1; }
                        to { transform: scale(2); opacity: 0; }
                    }
                    .animate-pop-burst { animation: pop-burst 0.35s ease-out forwards; }
                `}</style>
            </div>
        </div>
    );
};

export default BubblePopGame;
